import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	addPlace,
	deletePlaceImage,
	fetchPlaceDetailsAdmin,
	fetchSubCategoryByCategory,
	updatePlace
} from '../../../features/admin/placeSlice';
import { fetchRoomsByStays } from '../../../features/admin/roomSlice';
import Cookies from 'js-cookie';
import InnerLoading from '../../ui/InnerLoading';
import LocationPicker from './LocationPicker';
import AmenitiesSelector from '../ui/AmenitiesSelector';
import PlaceForm from './PlaceForm';
import AdditionalForm from './AdditionalForm';
import { deleteOptionalServiceFromPlace, fetchOptionalServices } from '../../../features/admin/additionalServiceSlice';
import { FaMinus } from 'react-icons/fa';
import { E164Number } from 'libphonenumber-js';
import { fetchServices } from '../../../features/admin/serviceSlice';
import { OptionalServiceType } from '../../../types';


const StayModal = () => {
	const { placeId } = useParams<{ placeId: string }>();
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate()
	const { place, subCategories, loading } = useSelector((state: RootState) => state.placeSlice);
	const { services } = useSelector((state: RootState) => state.serviceSlice);
	const { OptionalServices } = useSelector((state: RootState) => state.OptionalServiceSlice);
	const [showAddRoomsStep, setShowAddRoomsStep] = useState(false);
	const [errors, setErrors] = useState<any>({});
	const [createdPlaceId, setCreatedPlaceId] = useState<number | null>(null);
	const userId = Cookies.get('userId');
	const [additionalServices, setAdditionalServices] = useState<OptionalServiceType[]>([]);
	const [preview, setPreview] = useState<string[]>([]);
	const [formData, setFormData] = useState({
		title: '',
		titleAr: '',
		location: '',
		locationAr: '',
		discountPercentage: 0,
		paymentMethod: [] as number[],
		longitude: 0,
		latitude: 0,
		type: 'Stay',
		price: '',
		priceNight: "",
		discountNightPercentage: 0,
		numberOfPeoples: "",
		number: 0,
		subCategoryId: 0,
		isPopular: false,
		details: '',
		detailsAr: '',
		email: "",
		numberOfRooms: 0,
		numberOfHalls: 0,
		phoneNumber1: undefined as E164Number | undefined,
		phoneNumber2: "" as E164Number | "",
		amenities: [] as number[],
		images: [] as File[],
	});


	useEffect(() => {
		if (userId && placeId) {
			dispatch(fetchPlaceDetailsAdmin(parseInt(placeId)));
			dispatch(fetchRoomsByStays({ stayId: parseInt(placeId), userId }));
			dispatch(fetchOptionalServices({ placeId: parseInt(placeId), userId }));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [placeId, dispatch]);

	useEffect(() => {
		setAdditionalServices(OptionalServices);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		dispatch(fetchServices());
	}, [dispatch]);

	useEffect(() => {
		if (placeId && place) {
			setFormData({
				title: place.title || '',
				titleAr: place?.titleAr || '',
				location: place.location || '',
				locationAr: place.locationAr || '',
				discountPercentage: place.discountPercentage || 0,
				longitude: place.longitude || 0,
				latitude: place.latitude || 0,
				paymentMethod: place.paymentMethod || [],
				type: place.type || 'Stay',
				numberOfPeoples: place.numberOfPeoples ? String(place.numberOfPeoples) : "",
				price: place.price ? String(place.price) : "",
				priceNight: place.priceNight ? String(place.priceNight) : "",
				discountNightPercentage: place.discountNightPercentage || 0,
				isPopular: place.isPopular || false,
				number: place.number || 0,
				email: place.email || "",
				phoneNumber1: (place.phoneNumber1 as E164Number) || undefined,
				phoneNumber2: place.phoneNumber2 || "",
				details: place.details || '',
				detailsAr: place?.detailsAr || '',
				subCategoryId: place.subCategory?.id || 1,
				numberOfRooms: place.numberOfRooms || 0,
				numberOfHalls: place.numberOfHalls || 0,
				amenities: place.amenities?.map((a: any) => a.id) || [],
				images: [],
			});
			setPreview(place.images?.map((img: any) => img.url) || []);
		}
	}, [place, placeId]);

	// نوع المكان (فندق أو قاعة)
	useEffect(() => {
		if (formData.type === "Hall") {
			dispatch(fetchSubCategoryByCategory(4));
		} else {
			dispatch(fetchSubCategoryByCategory(1));
		}
	}, [formData.type, dispatch]);


	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			const files = Array.from(e.target.files);
			setFormData((prev) => ({
				...prev,
				images: [...prev.images, ...files],
			}));
			setPreview((prev) => [
				...prev,
				...files.map((file) => URL.createObjectURL(file)),
			]);
		}
	};

	const handleOptionalServiceDelete = (id: number) => {

		if (placeId && additionalServices.some(s => s.id === id)) {
			dispatch(deleteOptionalServiceFromPlace({ placeId: parseInt(placeId), id }))
				.unwrap()
				.then(() => {
					setAdditionalServices(prev => prev.filter(s => s.id !== id));
				})
		} else {
			setAdditionalServices(prev => prev.filter(s => s.id !== id));
		}
	};

	const handleImageDelete = (idx: number) => {
		const Preview = preview[idx];
		const isExistingImage = Preview.startsWith('http');

		if (isExistingImage && place?.images) {

			const dbImage = place.images.find((img: any) => img.url === Preview);
			if (dbImage && dbImage.id && placeId) {
				dispatch(deletePlaceImage({ placeId: Number(placeId), imageId: dbImage.id }));
			}
		}
		setPreview((prev) => prev.filter((_, i) => i !== idx));
		if (!isExistingImage) {
			setFormData((prev) => ({
				...prev,
				images: prev.images.filter((_, i) => i !== idx),
			}));
		}
	};
	const validateForm = () => {
		let newErrors: any = {};
		if (!formData.title?.trim()) newErrors.title = t("required");
		if (!formData.titleAr?.trim()) newErrors.titleAr = t("required");
		if (!formData.location?.trim()) newErrors.location = t("required");
		if (!formData.locationAr?.trim()) newErrors.locationAr = t("required");
		if (!formData.details?.trim()) newErrors.details = t("required");
		if (!formData.detailsAr?.trim()) newErrors.detailsAr = t("required");
		if (!formData.email?.trim()) newErrors.email = t("required");
		if (!formData.phoneNumber1?.trim() || formData.phoneNumber1?.length < 9) newErrors.phoneNumber1 = t("required");
		if (!formData.phoneNumber2?.trim() || formData.phoneNumber2?.length < 9) newErrors.phoneNumber2 = t("required");
		if (!formData.detailsAr?.trim()) newErrors.detailsAr = t("required");
		if (!formData.price || Number(formData.price) < 1) newErrors.price = t("price-must-be-at-least-1");

		if (formData?.type === "Hall" && (!formData.numberOfPeoples || Number(formData.numberOfPeoples) < 1)) newErrors.numberOfPeoples = t("required");
		if (formData?.isPopular && (!formData.number || Number(formData.number) < 1)) newErrors.number = t("required");
		// ===== Stay specific (Rooms & Halls) =====
		if (formData.type === "Hall") {
			if (!formData.priceNight || Number(formData.priceNight) < 1) newErrors.priceNight = t("price-must-be-at-least-1");
		}
		if (
			!formData.paymentMethod ||
			!Array.isArray(formData.paymentMethod) ||
			formData.paymentMethod.length === 0
		) {
			newErrors.paymentMethods = t("at-least-one-payment-method");
		}
		if (formData.type === "Stay" && Number(formData.subCategoryId) !== 1
		) {
			if (!formData.numberOfRooms || Number(formData.numberOfRooms) < 1) {
				newErrors.numberOfRooms = t("required");
			}

			if (!formData.numberOfHalls || Number(formData.numberOfHalls) < 0) {
				newErrors.numberOfHalls = t("required");
			}
		}
		if ((formData.images.length === 0) && (preview.length === 0)) {
			newErrors.images = t("at-least-one-image");
		}
		if (formData.amenities.length === 0) {
			newErrors.services = t("at-least-one-service");
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleNextStep = async () => {
		if (formData.title.trim().length < 5) return toast.error(t("title-too-short"));
		if (!formData.subCategoryId) return toast.error(t("select-subCategory"));
		if (!validateForm()) return;
		const data = new FormData();
		Object.entries(formData).forEach(([key, value]) => {
			if (key === "amenities") {
				(value as number[]).forEach((id) => data.append("amenities", String(id)));
			} else if (key === "images") {
				(value as File[]).forEach((file) => data.append("images", file));
			} else if (
				["price", "numberOfPeoples",].includes(key)
			) {
				data.append(key, String(Number(value)));
			} else if (key === "paymentMethod") {
				(value as number[]).forEach((id) =>
					data.append("paymentMethod", String(id))
				);
			} else {
				data.append(key, String(value));
			}
		});

		additionalServices.forEach((additionalService) => {
			data.append("OptionalServices", String(additionalService.id));
		});
		console.log(data);
		const res = await dispatch(addPlace(data));
		const newPlaceId = res.payload;
		setCreatedPlaceId(newPlaceId);

		if (formData.subCategoryId === 1) {
			setShowAddRoomsStep(true)
		} else {
			navigate(`/dashboard/place-details/${newPlaceId}`);
		}
	}
	const handleUpdatePlace = async () => {
		console.log(errors)
		if (!validateForm()) return;
		if (place && placeId) {
			const data = new FormData();
			Object.entries(formData).forEach(([key, value]) => {
				if (key === "amenities") {
					(value as number[]).forEach((id) => data.append("amenities", String(id)));
				} else if (key === "images") {
					(value as File[]).forEach((file) => data.append("images", file));
				} else if (
					["price", "numberOfPeoples", "priceNight"].includes(key)
				) {
					data.append(key, String(Number(value)));
				} else if (key === "paymentMethod") {
					(value as number[]).forEach((id) =>
						data.append("paymentMethod", String(id))
					);
				} else {
					data.append(key, String(value));
				}
			});
			additionalServices.forEach((additionalService) => {
				data.append("OptionalServices", String(additionalService.id));
			});

			try {
				await dispatch(updatePlace({ id: place.id, data })).unwrap();
				navigate(`/dashboard/place-details/${placeId}`);
			} catch (err: any) {
				toast.error(t("update-failed"));
			}

		}
	};
	if (loading) return <InnerLoading />;

	return (
		<div >
			<div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6'>
				<h2 className='text-white text-lg font-semibold'>{placeId ? t("edit-place") : t("create-place")}</h2>
			</div>
			<div className='w-full bg-white p-4 rounded-lg pt-10'>

				<>
					<PlaceForm
						formData={formData}
						setFormData={setFormData}
						subCategories={subCategories}
						errors={errors}
					/>
					<AmenitiesSelector
						amenities={services}
						selectedAmenities={formData.amenities}
						setSelectedAmenities={(value) => setFormData({ ...formData, amenities: value })}
					/>
					{errors.services && <p className="text-red-500 text-sm">{errors.services}</p>}
					<LocationPicker
						latitude={formData.latitude}
						longitude={formData.longitude}
						onChange={(coords) => setFormData((prev) => ({
							...prev,
							latitude: coords.latitude,
							longitude: coords.longitude,
						}))}
					/>
					<div className="mb-6">
						<label className="block text-gray-600 mb-2">{t("upload-images")}</label>
						<div className="flex items-center gap-4 mb-4 flex-wrap">
							{preview.map((src, idx) => (
								<div
									key={idx}
									className="relative w-28 h-28 rounded-lg border border-gray-300 overflow-hidden shadow-sm group"
								>
									<img
										src={src}
										alt={"image-perview"}
										className="w-full h-full object-cover"
									/>
									<button
										onClick={() => handleImageDelete(idx)}
										className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
									>
										✕
									</button>
								</div>
							))}
							<label className="w-28 h-28 flex items-center justify-center border border-dashed border-gray-400 rounded-lg cursor-pointer hover:border-primary transition">
								<span className="text-gray-500 text-sm text-center px-2">{t("add-image")}</span>
								<input
									type="file"
									accept="image/*"
									multiple
									onChange={handleImageChange}
									className="hidden"
								/>
							</label>
						</div>
						{errors.images && <p className="text-red-500 text-sm">{errors.images}</p>}
					</div>

					{additionalServices.length > 0 && (
						<div className="my-4 border p-3 rounded-lg">
							<h4 className="font-semibold mb-2">{t("additional-services-added")}</h4>
							<ul className="space-y-2">
								{additionalServices.map((service) => (
									<li
										key={service.id}
										className="border p-2 rounded bg-gray-50 flex justify-between items-center"
									>
										<div>
											<p className="font-medium">{service.title}</p>
											<p className="text-sm text-gray-600">{service.details}</p>
										</div>
										<div>
											<span className="text-primary font-bold">{service.price} {service.currencyCode} </span>
											<button
												onClick={() => service.id && handleOptionalServiceDelete(service.id)}
												className="bg-red-500 text-white p-1 rounded-full text-xs"
											>
												<FaMinus />
											</button>
										</div>

									</li>
								))}
							</ul>
						</div>
					)}
					<AdditionalForm additionalServices={additionalServices} setAdditionalServices={setAdditionalServices} />
					<div className="flex justify-end mt-6">
						<button
							onClick={placeId ? handleUpdatePlace : handleNextStep}
							disabled={loading}
							className={"bg-primary text-white px-8 py-3 rounded-lg " + (loading ? "opacity-50 cursor-not-allowed" : "")}
						>
							{placeId ? t("update") : t("next")}
						</button>
					</div>
				</>
				{
					showAddRoomsStep &&
					<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-5 z-50">
						<div className="bg-white p-6 rounded-xl w-full max-w-sm shadow-lg space-y-4">
							<div className=' flex gap-10 flex-col items-center justify-between w-full '>
								<div className=' text-center'>
									<h2 className='text-xl font-semibold mb-2'>{t("successfully")}</h2>
									<p className=' mb-2'>{t("hotel-added")}</p>
									<div className=' w-full flex-center'>
										<div className=' w-24'>
											<img src="/asset/success.png" className=' w-full' alt="success" loading='lazy' />
										</div>
									</div>

								</div>
								<Link className=' text-white bg-primary rounded-lg py-2 text-center w-full' to={`/dashboard/place-rooms/${createdPlaceId}`}>{t("continue")}</Link>
							</div>
						</div>
					</div>
				}
			</div>
		</div>
	);
};

export default StayModal;


